import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CargoDialogResult } from '@cargo/models/cargo-dialog-result';
import { CargoHostDialogParams } from '@cargo/models/cargo-host-dialog-params';
import { Logger } from '@core/services/logger.service';
import { timer } from 'rxjs';
import { SubSink } from 'subsink';

type CargoMessage = {
	message_id: string | undefined;
	data: string | undefined;
}

type CargoFileStatus = {
	hasProgress: boolean | undefined;
	isFinished: boolean | undefined;
	statusName: string | undefined;
};

type CargoFile = {
	error: string | null | undefined;
	fileID: string | undefined;
	name: string | undefined;
	preview: string | undefined;
	progress: number | undefined;
	size: number | undefined;
	status: CargoFileStatus | undefined;
};

type CargoSubmitStartMessageData = {
	RialtoID: string | undefined;
	Width: string | undefined;
	Height: string | undefined;
	fileCount: string | undefined;
	cargoFileList: CargoFile[] | undefined;
	cargoRequest: string | undefined;
	version: string | undefined;
};

@Component({
	selector: 'cass-cargo-host-dialog',
	templateUrl: './cargo-host-dialog.component.html',
	styleUrls: ['./cargo-host-dialog.component.css']
})
export class CargoHostDialogComponent implements AfterViewInit, OnDestroy {

	private readonly COMPONENT_NAME = "CargoHostDialogComponent";

	@ViewChild('cargoIframe') iframe: ElementRef | undefined;

	public cargoUrl: SafeResourceUrl | undefined;
	public titleTranslationKey: string = "";
	protected isLoading: boolean = true;

	private _dialogResult = new CargoDialogResult();

	private readonly _cargoOrigin: string | undefined;

	private readonly _CHECK_CARGO_FORM_LOADED_INTERVAL = 10e3; // msec
	private _isCargoEventReceived: boolean = false;
	private _subs = new SubSink();

	constructor (
		@Inject(MAT_DIALOG_DATA) private _dialogParams: CargoHostDialogParams,
		private _dialogRef: MatDialogRef<CargoHostDialogComponent>,
		private _logger: Logger,
		private _sanitizer: DomSanitizer
	) {
		if (!this._dialogParams || !this._dialogParams.cargoFormUrl) {
			this._logger.error(this.COMPONENT_NAME, "The dialog was created but Cargo URL was not provided");
			return;
		}

		this.cargoUrl = this._sanitizer.bypassSecurityTrustResourceUrl(this._dialogParams.cargoFormUrl);
		this.titleTranslationKey = this._dialogParams.titleTranlationKey;

		const parsedUrl = new URL(this._dialogParams.cargoFormUrl);
		this._cargoOrigin = `${parsedUrl.protocol}//${parsedUrl.host}`;
	}

	ngAfterViewInit() {
		if (this.iframe) {
			this.iframe.nativeElement.onload = () => {
				this.isLoading = false;
			};
		}
		else {
			this.isLoading = false; // hide the progress indicator 
			this._logger.error(this.COMPONENT_NAME, "The iframe is unavailable, Cargo messages might not be handled.");
		}

		window.addEventListener('message', this.handleMessage.bind(this), false);

		this._subs.add(
			timer(this._CHECK_CARGO_FORM_LOADED_INTERVAL).subscribe(() => {
				if (!this._isCargoEventReceived) {
					// report slow loading or failure to load
					this._logger.error(this.COMPONENT_NAME, `The Cargo form is not loaded or loads too slow (more than ${Math.floor(this._CHECK_CARGO_FORM_LOADED_INTERVAL / 1000)} sec)`);
				}
			})
		);
	}

	ngOnDestroy() {
		window.removeEventListener('message', this.handleMessage.bind(this), false);
		this._subs.unsubscribe();
	}

	handleMessage(event: MessageEvent) {
		// Handle only events sent by Cargo
		if (this._cargoOrigin && this._cargoOrigin !== event.origin) {
			return;
		}

		if (!event.isTrusted) {
			return;
		}

		const cargoMessageData = event.data as CargoMessage;

		if (!cargoMessageData) {
			this._logger.warning(this.COMPONENT_NAME, "Received a Cargo message with empty data");
			return;
		}

		this._isCargoEventReceived = true;

		switch (cargoMessageData.message_id) {
			case "cargo_submit_start":
				if (this._dialogParams.isPreviewRequired) {
					// expected to conatin the preview url
					const sumbitStartMessageData = this.decodeSumbitStartMessageData(cargoMessageData.data);
					if (sumbitStartMessageData) {
						if (sumbitStartMessageData.fileCount && sumbitStartMessageData.cargoFileList && sumbitStartMessageData.cargoFileList.length > 0) {
							const cargoFile = sumbitStartMessageData.cargoFileList[0];
							if (cargoFile && cargoFile.preview) {
								this._dialogResult.previewURL = cargoFile.preview;
							}
						}
					}
				}
				break;
			case "cargo_submit":
				// completes the session with the Cargo system
				this._dialogRef?.close(this._dialogResult);
				break;
			case "close":
				break;
			default:
				break;
		}
	}

	private decodeSumbitStartMessageData(data: string | undefined): CargoSubmitStartMessageData | undefined {
		if (!data) {
			return undefined;
		}

		try {
			return JSON.parse(data) as CargoSubmitStartMessageData;
		}
		catch (e) {
			this._logger.error(this.COMPONENT_NAME, `Cannot parse a Cargo submit start message data (JSON), error=${e}`);
			return undefined;
		}
	}
}
